import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Award, ArrowRight, Play, CheckCircle2, Calendar, Shield, Target, Book, Trophy, Zap, Bot, Code, Crosshair, Palette, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import EditableFaqList from '../components/EditableFaqList';
import { mockCurriculum, mockFeaturedRobotProjects } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import RobotGallery from '../components/RobotGallery';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import IconBadge from '../components/IconBadge';
import SectionHeader from '../components/SectionHeader';
import StepCards from '../components/StepCards';
import FeatureCard from '../components/FeatureCard';
import TestimonialsSection from '../components/TestimonialsSection';
import CtaBanner from '../components/CtaBanner';
import { useTestimonials } from '../hooks/useTestimonials';
import { useBrandName, useSettings } from '../context/SettingsContext';
import EditableText from '../components/EditableText';
import { BACKEND_URL, BTN_PRIMARY } from '../config/brand';
import PlanningSlots from '../components/PlanningSlots';
import { IMAGES } from '../config/images';

const CURSUS_OFFER_SLUG = 'cursus-annuel';

const CURSUS_PAGE_ANCHORS = [
  { id: 'deroule-cours', editKey: 'cursus.anchors.course', defaultValue: 'Comment se déroule un cours ?' },
  { id: 'planning', editKey: 'cursus.anchors.planning', defaultValue: 'Planning et horaires' },
  { id: 'galerie', editKey: 'cursus.anchors.gallery', defaultValue: 'Les projets des élèves' },
  { id: 'faq', editKey: 'cursus.anchors.faq', defaultValue: 'Questions fréquentes' },
];

function scrollToCursusAnchor(event, id) {
  event.preventDefault();
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.replaceState(null, '', `#${id}`);
}

function CursusPageAnchors({ className = '' }) {
  return (
    <nav aria-label="Aller à une section" className={className}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#3a00e0]">
        <EditableText editKey="cursus.anchors.eyebrow" defaultValue="En savoir plus" as="span" />
      </p>
      <ul className="space-y-2.5">
        {CURSUS_PAGE_ANCHORS.map((anchor) => (
          <li key={anchor.id}>
            <a
              href={`#${anchor.id}`}
              onClick={(event) => scrollToCursusAnchor(event, anchor.id)}
              className="group inline-flex items-center gap-2 text-[15px] font-medium text-slate-700 transition-colors hover:text-[#3a00e0]"
            >
              <ArrowRight className="h-4 w-4 shrink-0 text-[#fab03a] transition-transform group-hover:translate-x-0.5" />
              <EditableText
                editKey={anchor.editKey}
                defaultValue={anchor.defaultValue}
                as="span"
                className="underline decoration-[#fab03a]/80 decoration-2 underline-offset-4"
              />
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

const cursusFAQ = (brandName) => [
  {
    question: "Mon enfant peut-il rejoindre en cours d'année ?",
    answer: "Oui, selon les places disponibles et le niveau de votre enfant. Nous proposons une séance de rattrapage pour qu'il soit à niveau avec le groupe."
  },
  {
    question: "Que se passe-t-il si mon enfant manque plusieurs séances ?",
    answer: "Accès illimité à notre plateforme e-learning pour rattraper les cours manqués. Des séances de rattrapage sont également organisées pendant les vacances."
  },
  {
    question: "Le matériel est-il fourni ?",
    answer: "Oui, tout le matériel (robots, capteurs, outils) est fourni sur place pendant les séances. Les enfants n'emportent pas de kit à la maison."
  },
  {
    question: "Y a-t-il une évaluation à la fin ?",
    answer: `Oui, votre enfant reçoit un certificat de compétences ${brandName} détaillant tout ce qu'il a appris. C'est un vrai plus pour son parcours scolaire !`
  },
  {
    question: "Puis-je payer en plusieurs fois ?",
    answer: "Absolument ! Nous proposons un paiement en 10 mensualités de 115€ sans frais. Vous pouvez aussi payer en 3 fois."
  },
];

const DAY_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

const CURSUS_STEPS = [
  { icon: Book, number: 1, title: 'Septembre - Décembre', description: 'Découverte et bases de la robotique. Votre enfant construit ses premiers robots et apprend la programmation visuelle.' },
  { icon: Zap, number: 2, title: 'Janvier - Mars', description: 'Perfectionnement. Projets plus complexes avec capteurs et actionneurs. Votre enfant code des robots autonomes.' },
  { icon: Trophy, number: 3, title: 'Avril - Juin', description: 'Projet final et certification. Votre enfant crée son propre robot et présente son projet. Certificat de compétences remis !' },
];

const CURSUS_SKILLS = [
  { icon: Bot, variant: 'purple', title: 'Construction', description: 'Assemblage de robots complexes avec capteurs et moteurs' },
  { icon: Code, variant: 'orange', title: 'Programmation', description: 'Scratch, Python ou Arduino selon le niveau' },
  { icon: Crosshair, variant: 'purple', title: 'Résolution', description: 'Logique, algorithmique et pensée critique' },
  { icon: Palette, variant: 'orange', title: 'Créativité', description: 'Projets personnalisés et imagination sans limites' },
];

const CURSUS_BENEFITS = [
  { icon: CheckCircle2, variant: 'purple', title: 'Matériel fourni sur place', description: "Robots, capteurs et outils sont mis à disposition pendant les séances. Rien à acheter ni à emporter." },
  { icon: Users, variant: 'orange', title: 'Petits groupes de 8-10', description: 'Suivi personnalisé avec des formateurs passionnés. Chaque enfant avance à son rythme.' },
  { icon: Award, variant: 'purple', title: 'Accès e-learning gratuit', description: 'Plateforme en ligne avec +50 modules vidéo pour réviser et aller plus loin à la maison.' },
];

const Cursus = () => {
  const brandName = useBrandName();
  const { settings } = useSettings();
  const { toast } = useToast();
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSent, setWaitlistSent] = useState(false);
  const [waitlist, setWaitlist] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    child_age: '',
    preferred_slot: '',
  });
  const faqItems = cursusFAQ(brandName);
  const testimonials = useTestimonials(3);
  // Cursus dynamiques depuis l'admin, avec repli sur les donnees mock
  const [cursusList, setCursusList] = useState(() => {
    const c = mockCurriculum[0];
    return [
      {
        id: c.id,
        title: 'Cursus DigiRobot',
        ageRange: '6-16 ans',
        description: '30 séances hebdomadaires — le programme s\'adapte à chaque enfant',
        price: c.price,
        pricePerMonth: c.pricePerMonth,
        benefits: c.benefits || [],
        badge: null,
      },
    ];
  });

  const [centerSchedules, setCenterSchedules] = useState([]);
  const [cursusOfferId, setCursusOfferId] = useState(null);

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadCursus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/offers/cursus-annuel`);
        if (!res.ok) return;
        const offer = await res.json();
        if (offer?.formulas?.length) {
          setCursusOfferId(offer.id);
          setCursusList(
            offer.formulas.map((f) => ({
              id: f.id,
              title: f.name,
              ageRange: f.description || '',
              description: f.description || '',
              price: f.price,
              pricePerMonth: f.price_per_month,
              benefits: f.benefits || [],
              badge: f.badge || null,
            }))
          );
        }
      } catch {
        // garde le fallback mock
      }
    };
    loadCursus();
  }, []);

  useEffect(() => {
    if (!BACKEND_URL || !cursusOfferId) return;
    const loadSchedules = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/schedules?offer_id=${cursusOfferId}`);
        if (!res.ok) return;
        const schedules = await res.json();
        const byCenter = {};
        for (const s of schedules.filter((x) => !x.closed && x.active !== false)) {
          if (!byCenter[s.center_id]) {
            byCenter[s.center_id] = {
              id: s.center_id,
              name: s.center_name,
              address: s.center_address || '',
              slots: [],
            };
          }
          byCenter[s.center_id].slots.push(s);
        }
        const grouped = Object.values(byCenter).map((center) => ({
          ...center,
          slots: center.slots.sort(
            (a, b) =>
              DAY_ORDER.indexOf(a.day_of_week?.toLowerCase()) -
              DAY_ORDER.indexOf(b.day_of_week?.toLowerCase())
          ),
        }));
        setCenterSchedules(grouped);
      } catch {
        // garde section vide
      }
    };
    loadSchedules();
  }, [cursusOfferId]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Landing Page Style */}
      <section className="relative pt-below-site-header pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(58,0,224,0.06)] via-white to-[rgba(250,176,58,0.08)]" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[rgba(58,0,224,0.1)] rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <Reveal animation="fade-up">
            <div>
              <EditableText
                editKey="cursus.hero.badge"
                defaultValue="Formation annuelle complète"
                as="span"
                className="inline-flex bg-[rgba(58,0,224,0.08)] text-[#3a00e0] border border-[rgba(58,0,224,0.18)] mb-4 text-sm font-medium rounded-full px-3 py-1"
              />
              
              <EditableText
                editKey="cursus.hero.title"
                defaultValue="Cursus Annuel Robotique & Programmation"
                as="h1"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight tracking-tight"
              />
              
              <EditableText
                editKey="cursus.hero.subtitle"
                defaultValue={`30 séances progressives sur une année scolaire. Votre enfant apprend la robotique et la programmation, encadré par ${brandName}.`}
                as="p"
                multiline
                className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl"
              />
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/reserver?type=cursus">
                  <Button size="lg" className={`w-full sm:w-auto ${BTN_PRIMARY} text-lg px-8 py-6`}>
                    <EditableText editKey="cursus.hero.cta" defaultValue="Réserver ma place" className="inline" />
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-3 sm:gap-6">
                {[
                  { value: '30', label: 'Séances', key: 'cursus.hero.stat.1.label' },
                  { value: '1150€', label: 'ou 115€/mois', key: 'cursus.hero.stat.2.label' },
                  { value: '6-16', label: 'ans', key: 'cursus.hero.stat.3.label' },
                ].map((stat) => (
                  <div key={stat.key} className="bg-white/80 border border-slate-200 rounded-xl p-3 sm:p-4 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-gradient-brand mb-1">{stat.value}</div>
                    <EditableText
                      editKey={stat.key}
                      defaultValue={stat.label}
                      as="div"
                      className="text-xs sm:text-sm text-slate-600"
                    />
                  </div>
                ))}
              </div>
            </div>
            </Reveal>
            
            <Reveal animation="fade-in" delay={120} className="relative mt-8 lg:mt-0">
              <div className="aspect-[4/3] sm:aspect-[5/4] lg:aspect-square rounded-2xl overflow-hidden border border-[rgba(58,0,224,0.18)] shadow-2xl shadow-[0_20px_40px_-20px_rgba(58,0,224,0.2)] ring-1 ring-white/5">
                <img 
                  src={IMAGES.cursus.hero}
                  alt={`Cursus annuel ${brandName}`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-2 sm:-right-4 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl p-3 sm:p-4 shadow-xl">
                <div className="flex items-center gap-2">
                  <IconBadge icon={Trophy} variant="orange" size="sm" />
                  <EditableText
                    editKey="cursus.hero.floating_badge"
                    defaultValue="Certificat inclus"
                    as="span"
                    className="text-slate-900 font-semibold text-sm"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CircuitDivider />

      {/* Video Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            eyebrow="Découvrir"
            eyebrowKey="cursus.video.eyebrow"
            title="Une année pour maîtriser la robotique"
            titleKey="cursus.video.title"
            subtitle="Parcours structuré, projets concrets et accompagnement personnalisé"
            subtitleKey="cursus.video.subtitle"
            reveal
          />
          
          <Reveal animation="scale-in">
          <div className="aspect-video rounded-2xl overflow-hidden border border-[rgba(58,0,224,0.18)] shadow-2xl shadow-[0_20px_40px_-20px_rgba(58,0,224,0.2)] relative group cursor-pointer ring-1 ring-white/5 card-hover-glow">
            <img 
              src={IMAGES.cursus.hero} 
              alt="Présentation du cursus annuel"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#18449e]/45 via-[#18449e]/15 to-transparent group-hover:from-[#18449e]/35 transition-all flex items-center justify-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-[#3a00e0] to-[#fab03a] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[0_12px_30px_-10px_rgba(58,0,224,0.4)]">
                <Play className="h-10 w-10 sm:h-12 sm:w-12 text-white ml-1" />
              </div>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Cursus unique */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Cursus"
            eyebrowKey="cursus.levels.eyebrow"
            title="Le cursus annuel DigiRobot"
            titleKey="cursus.levels.title"
            subtitle="Un seul cursus, de 6 à 16 ans — le programme s'adapte à chaque enfant"
            subtitleKey="cursus.levels.subtitle"
            reveal
          />

          <div className={`grid gap-6 lg:gap-10 ${cursusList.length > 1 ? 'md:grid-cols-2 lg:grid-cols-3' : 'max-w-lg mx-auto lg:max-w-4xl lg:grid-cols-[minmax(0,28rem)_1fr] lg:items-center'}`}>
            {cursusList.map((curriculum, index) => (
              <Reveal key={curriculum.id} animation="fade-up" delay={index * 100}>
              <Card className="bg-white border-slate-200 shadow-sm hover:border-[rgba(58,0,224,0.28)] hover:-translate-y-1 hover:shadow-xl hover:shadow-[0_12px_30px_-16px_rgba(58,0,224,0.2)] transition-all duration-300 relative overflow-hidden">
                <CardHeader>
                  {curriculum.ageRange && (
                    <Badge variant="outline" className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] w-fit mb-2">
                      {curriculum.ageRange}
                    </Badge>
                  )}
                  <CardTitle className="text-2xl text-slate-900">{curriculum.title}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {curriculum.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="flex items-baseline justify-between pt-4 border-t border-slate-200">
                    <div>
                      <div className="text-4xl font-bold text-slate-900">
                        {curriculum.price}€
                      </div>
                      {curriculum.pricePerMonth && (
                        <div className="text-sm text-slate-600">
                          ou {curriculum.pricePerMonth}€/mois
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 pt-4">
                    {(curriculum.benefits || []).slice(0, 4).map((benefit, i) => (
                      <div key={`${curriculum.id}-benefit-${i}`} className="flex items-start text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-[#3a00e0]" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <Link to={`/reserver?type=cursus&offer=${CURSUS_OFFER_SLUG}&id=${curriculum.id}`}>
                    <Button className={`w-full mt-4 ${BTN_PRIMARY}`}>
                      <EditableText editKey="cursus.card.cta" defaultValue="Réserver" className="inline" />
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              </Reveal>
            ))}
            {cursusList.length <= 1 && (
              <CursusPageAnchors className="px-1 lg:pl-2" />
            )}
          </div>
          {cursusList.length > 1 && (
            <CursusPageAnchors className="mt-10 flex flex-col items-center text-center" />
          )}
        </div>
      </section>

      {/* Calendrier et Disponibilités */}
      <section id="planning" className="scroll-mt-site-header py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Planning"
            eyebrowKey="cursus.planning.eyebrow"
            title="Horaires et centres disponibles"
            titleKey="cursus.planning.title"
            subtitle="Choisissez le centre le plus proche de chez vous"
            subtitleKey="cursus.planning.subtitle"
            reveal
          />

          <PlanningSlots
            slots={centerSchedules.flatMap((center) => center.slots)}
            emptyText="Les horaires seront bientôt disponibles. Contactez-nous pour plus d'informations."
            emptyKey="cursus.planning.empty"
            getReserveHref={(slot) =>
              `/reserver?type=cursus&offer=${CURSUS_OFFER_SLUG}${
                slot.formula_id ? `&formula=${slot.formula_id}` : ""
              }&center=${slot.center_id}&schedule=${slot.id}`
            }
          />

          <div className="mt-8 max-w-xl mx-auto">
            {!waitlistOpen && !waitlistSent ? (
              <div className="text-center">
                <Button
                  size="lg"
                  className={`${BTN_PRIMARY} whitespace-normal h-auto py-3 px-6 text-base`}
                  onClick={() => setWaitlistOpen(true)}
                >
                  <EditableText
                    editKey="cursus.waitlist.cta"
                    defaultValue="S'il n'y a plus de place pour le créneau de votre choix, cliquez ici"
                    as="span"
                    className="inline"
                  />
                </Button>
              </div>
            ) : waitlistSent ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/80 px-5 py-6 text-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" aria-hidden />
                <p className="text-slate-900 font-semibold">Demande enregistrée</p>
                <p className="text-sm text-slate-600 mt-1">
                  Nous vous recontactons dès qu&apos;une place se libère ou qu&apos;une alternative est possible.
                </p>
              </div>
            ) : (
              <form
                className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-4 shadow-sm"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!BACKEND_URL) {
                    toast({ title: 'Erreur', description: 'Service indisponible.', variant: 'destructive' });
                    return;
                  }
                  try {
                    setWaitlistLoading(true);
                    const res = await fetch(`${BACKEND_URL}/api/bookings`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        is_lead: true,
                        waitlist_request: true,
                        booking_type: 'cursus',
                        offer_slug: CURSUS_OFFER_SLUG,
                        parent_name: waitlist.parent_name.trim(),
                        parent_email: waitlist.parent_email.trim(),
                        parent_phone: waitlist.parent_phone.trim(),
                        child_name: waitlist.child_name.trim(),
                        child_age: waitlist.child_age ? parseInt(waitlist.child_age, 10) : null,
                        preferred_slot: waitlist.preferred_slot.trim() || null,
                      }),
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.detail || 'Envoi impossible');
                    }
                    setWaitlistSent(true);
                    setWaitlistOpen(false);
                    toast({ title: 'Demande envoyée', description: 'Nous vous recontactons rapidement.' });
                  } catch (error) {
                    toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
                  } finally {
                    setWaitlistLoading(false);
                  }
                }}
              >
                <div>
                  <EditableText
                    editKey="cursus.waitlist.title"
                    defaultValue="Liste d'attente"
                    as="h3"
                    className="text-lg font-semibold text-slate-900"
                  />
                  <EditableText
                    editKey="cursus.waitlist.subtitle"
                    defaultValue="Laissez vos coordonnées : on traite la demande à la main et on vous rappelle."
                    as="p"
                    className="text-sm text-slate-600 mt-1"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="waitlist-name">Votre nom *</Label>
                    <Input
                      id="waitlist-name"
                      required
                      value={waitlist.parent_name}
                      onChange={(e) => setWaitlist((p) => ({ ...p, parent_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="waitlist-email">Email *</Label>
                    <Input
                      id="waitlist-email"
                      type="email"
                      required
                      value={waitlist.parent_email}
                      onChange={(e) => setWaitlist((p) => ({ ...p, parent_email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="waitlist-phone">Téléphone *</Label>
                    <Input
                      id="waitlist-phone"
                      type="tel"
                      required
                      value={waitlist.parent_phone}
                      onChange={(e) => setWaitlist((p) => ({ ...p, parent_phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="waitlist-child">Prénom de l&apos;enfant</Label>
                    <Input
                      id="waitlist-child"
                      value={waitlist.child_name}
                      onChange={(e) => setWaitlist((p) => ({ ...p, child_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="waitlist-age">Âge</Label>
                    <Input
                      id="waitlist-age"
                      type="number"
                      min={6}
                      max={16}
                      value={waitlist.child_age}
                      onChange={(e) => setWaitlist((p) => ({ ...p, child_age: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="waitlist-slot">Créneau souhaité (jour / horaire / centre)</Label>
                    <Input
                      id="waitlist-slot"
                      placeholder="Ex. Mardi 17h — Nice Gambetta"
                      value={waitlist.preferred_slot}
                      onChange={(e) => setWaitlist((p) => ({ ...p, preferred_slot: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => setWaitlistOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={waitlistLoading} className={`flex-1 ${BTN_PRIMARY}`}>
                    {waitlistLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi…</>
                    ) : (
                      'Envoyer la demande'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section id="deroule-cours" className="scroll-mt-site-header py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Parcours"
            eyebrowKey="cursus.year.eyebrow"
            title="Comment se déroule l'année ?"
            titleKey="cursus.year.title"
            subtitle="Un parcours progressif de septembre à juin"
            subtitleKey="cursus.year.subtitle"
            reveal
          />
          <StepCards steps={CURSUS_STEPS} columns={3} keyPrefix="cursus.year" />
        </div>
      </section>

      <RobotGallery
        id="galerie"
        projects={mockFeaturedRobotProjects}
        limit={8}
        title="Un programme, des dizaines de projets"
        titleKey="cursus.gallery.title"
        subtitle="Au fil de l'année, les enfants construisent et programment des robots de plus en plus ambitieux, adaptés à leur niveau."
        subtitleKey="cursus.gallery.subtitle"
        cta={{ to: '/ateliers', label: 'Explorer les projets' }}
        ctaKey="cursus.gallery.cta"
      />

      {/* Ce que votre enfant va apprendre */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Compétences"
            eyebrowKey="cursus.skills.eyebrow"
            title="Ce que votre enfant va maîtriser"
            titleKey="cursus.skills.title"
            subtitle="Des compétences concrètes et valorisantes pour son avenir"
            subtitleKey="cursus.skills.subtitle"
            reveal
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {CURSUS_SKILLS.map((skill, i) => (
              <Reveal key={skill.title} animation="fade-up" delay={i * 60}>
                <FeatureCard
                  {...skill}
                  size="lg"
                  titleKey={`cursus.skills.${i + 1}.title`}
                  descriptionKey={`cursus.skills.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi choisir notre cursus */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos atouts"
            eyebrowKey="cursus.why.eyebrow"
            title="Pourquoi choisir notre cursus annuel ?"
            titleKey="cursus.why.title"
            reveal
          />

          <div className="grid md:grid-cols-3 gap-6">
            {CURSUS_BENEFITS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  centered={false}
                  titleKey={`cursus.why.${i + 1}.title`}
                  descriptionKey={`cursus.why.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection
        testimonials={testimonials}
        bgClassName=""
        eyebrowKey="cursus.testimonials.eyebrow"
        titleKey="cursus.testimonials.title"
        ratingKey="cursus.testimonials.rating"
      />

      {/* FAQ */}
      <section id="faq" className="scroll-mt-site-header py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            eyebrow="FAQ"
            eyebrowKey="cursus.faq.eyebrow"
            title="Questions fréquentes"
            titleKey="cursus.faq.title"
            subtitle="Tout ce que vous devez savoir sur le cursus annuel"
            subtitleKey="cursus.faq.subtitle"
            reveal
          />

          <EditableFaqList items={faqItems} keyPrefix="cursus.faq" />
        </div>
      </section>

      <CtaBanner
        title="Offrez à votre enfant une année extraordinaire !"
        titleKey="cursus.cta.title"
        subtitle="Réservez maintenant sa place dans notre cursus annuel. Paiement en 10 fois sans frais."
        subtitleKey="cursus.cta.subtitle"
        footer="Matériel fourni sur place · E-learning gratuit · Certificat de compétences"
        footerKey="cursus.cta.footer"
      >
        <Link to="/reserver?type=cursus">
          <Button size="lg" className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)] text-xl px-12 py-8">
            <EditableText editKey="cursus.cta.button" defaultValue="Réserver maintenant - 1150€" className="inline" />
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </Link>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Cursus;
